// Two-panel view of ICS input/output mappings — shows which raw materials are used to produce each HTE finished product.
// Left: searchable product list. Right: raw materials table with "Add Raw Material" modal for linking new materials.

import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Search, ArrowRightLeft, X, Plus } from 'lucide-react';
import inputOutputMappingService, { RawMaterialSearchResult } from '../../services/inputOutputMappingService';
import productionService from '../../services/productionService';
import { useLanguage } from '../../i18n/LanguageContext';
import { formatYield } from '../../hooks/useYieldFactor';
import { wsService } from '../../services/websocket';

const InputOutputMapping: React.FC = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [productSearch, setProductSearch] = useState('');

  // Check if editing is enabled via config_settings — plain state, no react-query cache
  const [editingEnabled, setEditingEnabled] = useState(false);

  // Fetch on mount + listen for real-time WS updates
  useEffect(() => {
    const fetchSetting = () => {
      productionService.getConfigSettings().then(all => {
        const setting = all.find(s => s.setting_key === 'enable_input_output_editing');
        setEditingEnabled(setting?.setting_value === 'true');
      }).catch(() => { console.warn('Failed to load enable_input_output_editing feature flag'); });
    };

    fetchSetting();

    wsService.connect();
    const handleConfigUpdate = (data: any) => {
      if (data?.key === 'enable_input_output_editing') {
        setEditingEnabled(data.value === 'true');
      }
    };
    wsService.on('config-updated', handleConfigUpdate);
    return () => { wsService.off('config-updated', handleConfigUpdate); };
  }, []);

  // Add-modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [rmSearch, setRmSearch] = useState('');
  const [rmSearchResults, setRmSearchResults] = useState<RawMaterialSearchResult[]>([]);
  const [rmSearching, setRmSearching] = useState(false);
  const [selectedRM, setSelectedRM] = useState<RawMaterialSearchResult | null>(null);
  const [preference, setPreference] = useState<number>(1);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Fetch products list
  const { data: products = [], isLoading: loadingProducts } = useQuery(
    ['io-mapping-products'],
    inputOutputMappingService.getProducts
  );

  // Fetch raw materials for selected product
  const { data: rawMaterials = [], isLoading: loadingMaterials } = useQuery(
    ['io-mapping-materials', selectedProduct],
    () => selectedProduct ? inputOutputMappingService.getMappings(selectedProduct) : Promise.resolve([]),
    { enabled: !!selectedProduct }
  );

  // Filter products by search
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products;
    const q = productSearch.toLowerCase();
    return products.filter(
      (p) =>
        p.item_number.toLowerCase().includes(q) ||
        (p.item_english_name || '').toLowerCase().includes(q) ||
        (p.item_chinese_name || '').toLowerCase().includes(q)
    );
  }, [products, productSearch]);

  const selectedProductInfo = products.find((p) => p.item_number === selectedProduct);

  // Set of already-linked raw material item numbers (for filtering search results)
  const linkedItemNumbers = useMemo(
    () => new Set(rawMaterials.map((rm) => rm.item_number)),
    [rawMaterials]
  );

  // Open add modal — default preference = max existing + 1
  const openAddModal = () => {
    const maxPref = rawMaterials.reduce((max, rm) => Math.max(max, rm.preference ?? 0), 0);
    setPreference(maxPref + 1);
    setRmSearch('');
    setRmSearchResults([]);
    setSelectedRM(null);
    setShowAddModal(true);
  };

  // Search raw materials (debounced via onKeyDown/onChange)
  const handleRmSearch = async (query: string) => {
    setRmSearch(query);
    if (query.trim().length < 2) {
      setRmSearchResults([]);
      return;
    }
    setRmSearching(true);
    try {
      const results = await inputOutputMappingService.searchRawMaterials(query);
      // Filter out already-linked materials
      setRmSearchResults(results.filter((r) => !linkedItemNumbers.has(r.item_number)));
    } catch {
      setRmSearchResults([]);
    } finally {
      setRmSearching(false);
    }
  };

  // Save new linkage
  const handleSave = async () => {
    if (!selectedProduct || !selectedRM) return;
    setSaving(true);
    try {
      await inputOutputMappingService.createMapping({
        product_item_number: selectedProduct,
        raw_material_item_number: selectedRM.item_number,
        preference,
      });
      // Refresh data
      queryClient.invalidateQueries(['io-mapping-materials', selectedProduct]);
      queryClient.invalidateQueries(['io-mapping-products']);
      setToast({ type: 'success', message: t('inputOutputMapping.mappingCreated') });
      setShowAddModal(false);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err.message || 'Error';
      setToast({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  // Auto-dismiss toast
  React.useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('inputOutputMapping.subtitle')}
        </p>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`mb-3 px-4 py-2 rounded-md text-sm ${
          toast.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Two-panel layout */}
      <div className="flex-1 flex flex-col md:flex-row gap-4 min-h-0">
        {/* Left panel: Products */}
        <div className="w-full md:w-1/3 flex flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t('inputOutputMapping.selectProduct')}
            </h3>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder={t('inputOutputMapping.searchProducts')}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingProducts ? (
              <div className="p-4 text-center text-sm text-gray-500">{t('common.loading')}</div>
            ) : filteredProducts.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                {t('inputOutputMapping.noResults')}
              </div>
            ) : (
              filteredProducts.map((p) => (
                <button
                  key={p.item_number}
                  onClick={() => setSelectedProduct(p.item_number)}
                  className={`w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    selectedProduct === p.item_number
                      ? 'bg-primary-50 dark:bg-primary-900/30 border-l-2 border-l-primary-500'
                      : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {p.item_english_name || p.item_number}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {p.item_number}
                        {p.item_chinese_name && ` · ${p.item_chinese_name}`}
                      </div>
                    </div>
                    <span className="ml-2 flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 rounded">
                      {p.raw_material_count}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right panel: Raw materials table */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          {!selectedProduct ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8 max-w-md">
                <ArrowRightLeft className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('inputOutputMapping.selectProductPrompt')}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Header with Add button */}
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('inputOutputMapping.rawMaterials')}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                    {selectedProductInfo?.item_english_name || selectedProduct}
                    {selectedProductInfo?.item_chinese_name && ` (${selectedProductInfo.item_chinese_name})`}
                  </p>
                </div>
                {editingEnabled ? (
                  <button
                    onClick={openAddModal}
                    className="ml-auto flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t('inputOutputMapping.addRawMaterial')}
                  </button>
                ) : (
                  <span className="ml-auto flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                    {t('common.readOnly')}
                  </span>
                )}
              </div>

              {/* Table */}
              <div className="flex-1 overflow-auto">
                {loadingMaterials ? (
                  <div className="p-4 text-center text-sm text-gray-500">{t('common.loading')}</div>
                ) : rawMaterials.length === 0 ? (
                  <div className="flex items-center justify-center p-8 text-gray-400 dark:text-gray-500">
                    <div className="text-center">
                      <ArrowRightLeft className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{t('inputOutputMapping.noRawMaterials')}</p>
                    </div>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">#</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('inputOutputMapping.itemNumber')}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('inputOutputMapping.name')}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('inputOutputMapping.yieldFactor')}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('inputOutputMapping.laborFactor')}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('inputOutputMapping.batchSize')}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('inputOutputMapping.teamSize')}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('inputOutputMapping.comments')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {rawMaterials.map((rm, idx) => (
                        <tr key={rm.item_number} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                            {rm.preference ?? idx + 1}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">
                            {rm.item_number}
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-gray-900 dark:text-gray-100">
                              {rm.item_english_name || '—'}
                            </div>
                            {rm.item_chinese_name && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {rm.item_chinese_name}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                            {rm.yield_factor != null && Number(rm.yield_factor) > 0
                              ? formatYield(Number(rm.yield_factor) / 100)
                              : '—'}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                            {rm.labor_factor != null && Number(rm.labor_factor) > 0 ? Number(rm.labor_factor).toFixed(2) : '—'}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                            {rm.batch_size != null && Number(rm.batch_size) > 0 ? rm.batch_size : '—'}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                            {rm.team_size != null && Number(rm.team_size) > 0 ? rm.team_size : '—'}
                          </td>
                          <td className="px-3 py-2 text-gray-500 dark:text-gray-400 max-w-[200px] truncate">
                            {rm.comments || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add Raw Material Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('inputOutputMapping.addRawMaterial')}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-5 py-4 flex-1 overflow-y-auto space-y-4">
              {/* Product info */}
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedProductInfo?.item_english_name || selectedProduct}
                {selectedProductInfo?.item_chinese_name && ` (${selectedProductInfo.item_chinese_name})`}
              </div>

              {/* Search raw materials */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('inputOutputMapping.searchRawMaterials')}
                </label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={rmSearch}
                    onChange={(e) => handleRmSearch(e.target.value)}
                    placeholder={t('inputOutputMapping.searchRawMaterials')}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              {/* Selected item */}
              {selectedRM && (
                <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700 rounded-md">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {selectedRM.item_english_name || selectedRM.item_number}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {selectedRM.item_number}
                      {selectedRM.item_chinese_name && ` · ${selectedRM.item_chinese_name}`}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedRM(null)}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Search results list */}
              {!selectedRM && rmSearch.trim().length >= 2 && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-md max-h-48 overflow-y-auto">
                  {rmSearching ? (
                    <div className="p-3 text-center text-sm text-gray-500">{t('common.loading')}</div>
                  ) : rmSearchResults.length === 0 ? (
                    <div className="p-3 text-center text-sm text-gray-500">{t('inputOutputMapping.noResults')}</div>
                  ) : (
                    rmSearchResults.map((item) => (
                      <button
                        key={item.item_number}
                        onClick={() => setSelectedRM(item)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                      >
                        <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
                          {item.item_english_name || item.item_number}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {item.item_number}
                          {item.item_chinese_name && ` · ${item.item_chinese_name}`}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}

              {/* Preference input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('inputOutputMapping.preference')}
                </label>
                <input
                  type="number"
                  min={1}
                  value={preference}
                  onChange={(e) => setPreference(parseInt(e.target.value) || 1)}
                  className="w-24 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={!selectedRM || saving}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? t('common.processing') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InputOutputMapping;
